/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
'use strict'

import { useEffect, useRef, useState } from 'react';
import { Button, Form, Dropdown } from 'react-bootstrap';

function AddCategoryDialog({ editFp, setEditFp, catData } : any) {
    const [filter, setFilter] = useState<string>("");
    const [show, setShow] = useState<boolean>(false);
    const [dropDirection, setDropDirection] = useState<"up" | "down">("down");
    const [dropAlign, setDropAlign] = useState<"start" | "end">("start");
    const toggleRef = useRef<HTMLElement | null>(null);
    const filterRef = useRef<HTMLInputElement | null>(null);

    const filterLower = filter.toLowerCase();

    const getMatchPriority = (title: string) => {
        if (!filterLower) {
            return 3;
        }
        if (title.startsWith(filterLower)) {
            return 1;
        }
        if (title.indexOf(' ' + filterLower) !== -1) {
            return 2;
        }
        return 3;
    };

    const matchesFilter = (cat: any) => {
        if (filter.trim() === "") {
            return true;
        }

        const title = cat.title.toLowerCase();
        const key = cat.key.toLowerCase();
        if (filter.trim().length === 1) {
            return title.startsWith(filterLower) || key.startsWith(filterLower);
        }

        return title.includes(filterLower) || key.includes(filterLower);
    };

    const filteredCategories = catData
        .filter(matchesFilter)
        .sort((a: any, b: any) => {
            const aTitleLower = a.title.toLowerCase();
            const bTitleLower = b.title.toLowerCase();

            const aPriority = getMatchPriority(aTitleLower);
            const bPriority = getMatchPriority(bTitleLower);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            return aTitleLower.localeCompare(bTitleLower);
        });

    const handleToggleClick = () => {
        if (show) {
            setShow(false);
            return;
        }

        const rect = toggleRef.current?.getBoundingClientRect();
        const menuHeight = 320;
        const menuWidth = 320;
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const spaceRight = window.innerWidth - rect.left;
            const spaceLeft = rect.right;
            setDropDirection(spaceBelow < menuHeight && spaceAbove > spaceBelow ? "up" : "down");
            setDropAlign(spaceRight >= menuWidth || spaceRight >= spaceLeft ? "start" : "end");
        }
        setShow(true);
    };

    useEffect(() => {
        if (show) {
            window.requestAnimationFrame(() => {
                filterRef.current?.focus({ preventScroll: true });
            });
        }
    }, [show, dropDirection]);

    const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
        const element = event.currentTarget;
        const scrollingUp = event.deltaY < 0;
        const scrollingDown = event.deltaY > 0;
        const atTop = element.scrollTop <= 0;
        const atBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;

        if ((scrollingUp && atTop) || (scrollingDown && atBottom)) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const renderFilterSection = () => (
        <div className="px-3 py-2">
            <Form.Control
              ref={filterRef}
              type="text"
              placeholder="Filter categories..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
              onClick={e => e.stopPropagation()}
              size="sm"
              className="w-100"
            />
        </div>
    );

    const renderCategoryRows = () => (
        <div>
            {filteredCategories.map((cat: any) => (
                <Dropdown.Item
                    key={cat.key}
                    className=""
                    onClick={() => {
                        setEditFp((prev: any) => {
                            const prevCategories = Array.isArray(prev?.categories) ? prev.categories : [];
                            if (prevCategories.includes(cat.key)) {
                                return prev;
                            }
                            return {
                                ...prev,
                                categories: [...prevCategories, cat.key]
                            } as any;
                        });
                        setFilter("");
                        setShow(false);
                    }}
                >
                    {cat.title}
                </Dropdown.Item>
            ))}
            {filteredCategories.length === 0 && (
                <Dropdown.Item disabled className="py-2">
                    No categories match
                </Dropdown.Item>
            )}
        </div>
    );

  return (
      <>
          <style>{`.category-add-toggle::after { display: none !important; }`}</style>
          <Dropdown show={show} onToggle={setShow} drop={dropDirection} align={dropAlign}>
              <Dropdown.Toggle
                ref={toggleRef}
                                as={Button}
                                variant="outline-success"
                                size="sm"
                id="category-dropdown"
                                className="ms-2 category-add-toggle"
                                style={{ display: 'inline-flex', alignItems: 'center', paddingTop: '0.1rem', paddingBottom: '0.1rem', lineHeight: 1, textDecoration: 'none' }}
                onClick={handleToggleClick}
              >
                                    <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Add</span>
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                    maxHeight: '320px',
                    minWidth: '320px',
                    border: '1px solid var(--bs-success)',
                    borderRadius: '0.375rem',
                    boxShadow: '0 0 0 0.2rem rgba(25, 135, 84, 0.12)',
                    padding: 0,
                    
                    overflow: 'hidden'
                }}
              >
                  <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '300px', minHeight: 0 }}>
                      {dropDirection === "down" ? renderFilterSection() : null}
                      <div
                        style={{
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                        }}
                        onWheel={handleListWheel}
                      >
                          {renderCategoryRows()}
                      </div>
                      {dropDirection === "up" ? renderFilterSection() : null}
                  </div>
              </Dropdown.Menu>
          </Dropdown>
      </>
  );
}

export default AddCategoryDialog;
